import React, { useState, useEffect, useRef } from 'react';
import { Dayjs } from 'dayjs';
import dayjs from 'dayjs';

export interface WeekCalendarPickerProps {
  selectedWeek: Dayjs[];
  onSelectWeek: (newWeek: Dayjs[]) => void;
}

const MONTH_NAMES = ['January','February','March','April','May','June',
  'July','August','September','October','November','December'];

function isoWeekNum(d: Date): number {
  const tmp = new Date(d.valueOf());
  const day = (d.getDay() + 6) % 7;
  tmp.setDate(tmp.getDate() - day + 3);
  const firstThu = new Date(tmp.getFullYear(), 0, 4);
  return 1 + Math.round(((tmp.getTime() - firstThu.getTime()) / 86400000 - 3 + ((firstThu.getDay() + 6) % 7)) / 7);
}

export function getMondayOf(d: Date): Date {
  const t = new Date(d);
  const day = (t.getDay() + 6) % 7;
  t.setDate(t.getDate() - day);
  t.setHours(0, 0, 0, 0);
  return t;
}

function sameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export function buildWeekFromMonday(mon: Date): Dayjs[] {
  return Array.from({ length: 7 }, (_, i) => dayjs(mon).add(i, 'day'));
}

const WeekCalendarPicker: React.FC<WeekCalendarPickerProps> = ({ selectedWeek, onSelectWeek }) => {
  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(dayjs().year());
  const [viewMonth, setViewMonth] = useState(dayjs().month());
  const [yearMode, setYearMode] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleToggle = () => {
    if (!open && selectedWeek.length > 0) {
      setViewYear(selectedWeek[0].year());
      setViewMonth(selectedWeek[0].month());
      setYearMode(false);
    }
    setOpen(p => !p);
  };

  const selMonTime = selectedWeek.length > 0 ? getMondayOf(selectedWeek[0].toDate()).getTime() : 0;
  const today = new Date(); today.setHours(0, 0, 0, 0);

  const renderMonth = () => {
    const firstDay = new Date(viewYear, viewMonth, 1);
    const startMon = getMondayOf(firstDay);
    const rows: { weekMon: Date; days: Date[] }[] = [];
    const cur = new Date(startMon);
    for (let w = 0; w < 6; w++) {
      const weekMon = new Date(cur);
      const days: Date[] = [];
      for (let d = 0; d < 7; d++) { days.push(new Date(cur)); cur.setDate(cur.getDate() + 1); }
      rows.push({ weekMon, days });
      if (new Date(cur).getMonth() > viewMonth || new Date(cur).getFullYear() > viewYear) {
        if (w >= 3) break;
      }
    }
    return rows.map(({ weekMon, days }) => {
      const isSelected = weekMon.getTime() === selMonTime;
      const wn = isoWeekNum(weekMon);
      return (
        <div
          key={weekMon.getTime()}
          onClick={() => { onSelectWeek(buildWeekFromMonday(weekMon)); setOpen(false); }}
          style={{
            display: 'grid', gridTemplateColumns: '26px repeat(7,1fr)', gap: 2,
            borderRadius: 10, cursor: 'pointer', padding: '1px 0',
            background: isSelected ? 'rgba(37,99,235,.12)' : undefined,
            position: 'relative',
          }}
          onMouseEnter={e => { if (!isSelected) (e.currentTarget as HTMLElement).style.background = 'var(--hover-new)'; }}
          onMouseLeave={e => { if (!isSelected) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
        >
          {isSelected && (
            <div style={{ position: 'absolute', left: 0, top: '10%', bottom: '10%', width: 3, borderRadius: 2, background: 'linear-gradient(180deg,#2563eb,#7c3aed)' }} />
          )}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, fontWeight: 800, color: isSelected ? '#2563eb' : 'var(--text-color-calendar-week)' }}>W{wn}</div>
          {days.map(d => {
            const otherMonth = d.getMonth() !== viewMonth;
            const isToday = sameDay(d, today);
            return (
              <div key={d.getTime()} style={{
                textAlign: 'center', padding: '5px 2px', borderRadius: 7,
                fontSize: 12, fontWeight: isToday ? 900 : isSelected ? 700 : 600,
                color: isToday ? 'white' : otherMonth ? 'var(--text-color-calendar-week)' : isSelected ? '#2563eb' : 'var(--text-color-calendar)',
                background: isToday ? 'linear-gradient(135deg,#2563eb,#7c3aed)' : undefined,
                opacity: otherMonth ? .4 : 1,
              }}>{d.getDate()}</div>
            );
          })}
        </div>
      );
    });
  };

  const renderYears = () => {
    const start = viewYear - 6;
    return Array.from({ length: 16 }, (_, i) => start + i).map(y => (
      <div
        key={y}
        onClick={() => { setViewYear(y); setYearMode(false); }}
        style={{
          padding: '7px 4px', borderRadius: 9, textAlign: 'center',
          fontSize: 12, fontWeight: y === viewYear ? 900 : 700,
          color: y === viewYear ? '#2563eb' : 'var(--text-color-calendar)',
          background: y === viewYear ? 'rgba(37,99,235,.1)' : undefined,
          cursor: 'pointer',
        }}
        onMouseEnter={e => { if (y !== viewYear) (e.currentTarget as HTMLElement).style.background = 'var(--hover-new)'; }}
        onMouseLeave={e => { if (y !== viewYear) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
      >{y}</div>
    ));
  };

  const selLabel = selectedWeek.length > 0
    ? `${selectedWeek[0].format('D MMM')} – ${selectedWeek[6].format('D MMM YYYY')}`
    : '—';

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={handleToggle}
        title="Pick week"
        style={{
          width: 32, height: 32, borderRadius: 10, cursor: 'pointer',
          background: open ? 'rgba(37,99,235,.1)' : 'var(--btn-user-form)',
          border: open ? '1px solid rgba(37,99,235,.3)' : '1px solid var(--border-input)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: open ? '#2563eb' : 'var(--text-color)', transition: 'all .14s',
        }}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
        </svg>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 8px)', left: '50%', transform: 'translateX(-50%)',
          zIndex: 9999, width: 312, borderRadius: 16,
          background: 'var(--bg-form)', border: '1px solid var(--border-input)',
          boxShadow: '0 12px 40px rgba(0,0,0,.22)', overflow: 'hidden',
        }}>
          {!yearMode ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px 8px' }}>
                <button type="button" onClick={() => { let m = viewMonth - 1, y = viewYear; if (m < 0) { m = 11; y--; } setViewMonth(m); setViewYear(y); }}
                  style={{ width: 28, height: 28, borderRadius: 8, background: 'var(--btn-user-form)', border: '1px solid var(--border-input)', cursor: 'pointer', color: 'var(--text-color)', fontSize: 16 }}>‹</button>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span onClick={() => setYearMode(true)} style={{ fontSize: 15, fontWeight: 900, color: 'var(--text-color)', cursor: 'pointer' }}>{MONTH_NAMES[viewMonth]}</span>
                  <span onClick={() => setYearMode(true)} style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-color-calendar-week)', cursor: 'pointer', padding: '2px 7px', borderRadius: 6, background: 'var(--btn-user-form)' }}>{viewYear}</span>
                </div>
                <button type="button" onClick={() => { let m = viewMonth + 1, y = viewYear; if (m > 11) { m = 0; y++; } setViewMonth(m); setViewYear(y); }}
                  style={{ width: 28, height: 28, borderRadius: 8, background: 'var(--btn-user-form)', border: '1px solid var(--border-input)', cursor: 'pointer', color: 'var(--text-color)', fontSize: 16 }}>›</button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '26px repeat(7,1fr)', padding: '0 10px 4px', gap: 2 }}>
                <div />
                {['Mo','Tu','We','Th','Fr','Sa','Su'].map(d => (
                  <div key={d} style={{ textAlign: 'center', fontSize: 9, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text-color-calendar-week)', padding: '3px 0' }}>{d}</div>
                ))}
              </div>
              <div style={{ padding: '0 10px 10px', display: 'flex', flexDirection: 'column', gap: 2 }}>
                {renderMonth()}
              </div>
              <div style={{ padding: '8px 14px 12px', borderTop: '1px solid var(--border-input)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 11, color: 'var(--text-color-calendar-week)' }}>Selected:</span>
                <span style={{ fontSize: 11, fontWeight: 800, color: 'var(--text-color)' }}>{selLabel}</span>
              </div>
            </>
          ) : (
            <div style={{ padding: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--text-color-calendar-week)', letterSpacing: '.06em', textTransform: 'uppercase' }}>Year</span>
                <button type="button" onClick={() => setYearMode(false)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-color-calendar-week)', fontSize: 13 }}>← Back</button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 6 }}>
                {renderYears()}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WeekCalendarPicker;
